import { useMemo } from "react";
import { AutosaveStatusType, IReport, IFilterReportsProps } from "../models/interface";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import relativeTime from "dayjs/plugin/relativeTime";
import { fileStatus, IMetaDetails, ReportsHomePageTabType } from "src/pages/Studies/models/interfaces";
import { AppDispatch } from "src/store";
import { readStream } from "src/utils";
import { IDataConfig, IQueryMessage, IStreamResponse, QueryMessageType } from "src/pages/NewAskKural/models/interfaces";
import { cleanupAbortControllerReports, resetReportsReferences, setReportsReferences } from "../states/reportsSlice";

dayjs.extend(utc);
dayjs.extend(relativeTime);

export const formatAutoSaveTime = (timestamp) => {
	return timestamp ? dayjs.utc(timestamp).local().fromNow() : "";
	// return timestamp ? dayjs(timestamp).local().fromNow() : "";
	// return timestamp ? dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss') : "";
};

export const processSectionPreviewStreamResponse = async (
	response: Response,
	setMessages: React.Dispatch<React.SetStateAction<IQueryMessage[]>>,
	payload: any,
	dispatch: AppDispatch,
	setSections: any
): Promise<void> => {
	let combinedData = "";
	let isDataFromDB: boolean = false;
	let isNotificationMode = false;
	let isInterruptMode = false;
	let interruptData = "";

	try {
		for await (const line of readStream(response)) {
			if (line === "[SECTION_CONTENT]" || line === "[DONE]" || line === "[REFERENCES]") {
				continue;
			}
			if (line === "[END]") {
				if (combinedData && payload?.section_id) {
					setSections((prev) => {
						const updatedSections = prev?.map((sec) =>
							sec.section_id === payload?.section_id ? { ...sec, display_on_ui: fileStatus.success, is_edited: false } : sec
						);
						return updatedSections;
					});
				}
				break;
			}
			if (line === "[NOTIFICATION]") {
				isNotificationMode = true;
				dispatch(resetReportsReferences());
				setMessages((prevMessages) => {
					const existingIndex = prevMessages.findIndex((msg) => msg.sectionId === payload?.section_id);
					const existingOrder = existingIndex !== -1 ? prevMessages[existingIndex]?.order ?? null : null;

					const newNotification = {
						text: "",
						sectionId: payload?.section_id,
						type: QueryMessageType.received,
						is_notification: true,
						order: existingOrder,
					};

					if (existingIndex !== -1) {
						const updatedMessages = [...prevMessages];
						updatedMessages[existingIndex] = newNotification;
						return updatedMessages;
					} else {
						return [...prevMessages, newNotification];
					}
				});
				continue;
			}
			if (line === "[NOTIFICATION_DONE]") {
				isNotificationMode = false;
				continue;
			}
			if (line === "[INTERRUPT]") {
				isInterruptMode = true;
				interruptData = "";
				continue;
			}

			if (isInterruptMode && line === "[INTERRUPT_DONE]") {
				isInterruptMode = false;
				try {
					const stringInterrupt = JSON.stringify([]);
					const parsedInterrupts = JSON.parse(stringInterrupt);
					// Update combined messages with interrupt elements
				} catch (error) {
					console.error(`Error parsing interrupt data for:`, error);
				}
				interruptData = "";
				continue;
			}

			if (isInterruptMode) {
				interruptData += line;
				continue;
			}

			let parsedData: IStreamResponse;
			try {
				parsedData = JSON.parse(line);
			} catch (error) {
				console.error("Error parsing JSON:", error);
				continue;
			}

			// Handle error events
			if (parsedData.event === "error") {
				setSections((prev) => prev?.map((sec) => (sec.section_id === payload?.section_id ? { ...sec, display_on_ui: fileStatus.failed, is_edited: true } : sec)));

				// Set error message for the section
				setMessages((prevMessages) => {
					const existingIndex = prevMessages.findIndex((msg) => msg.sectionId === payload?.section_id);

					const errorMessage = {
						text: parsedData.data || "An error occurred while generating this section.",
						sectionId: payload?.section_id,
						type: QueryMessageType.received,
						is_notification: false,
						is_error: true, // Optional: flag to identify error messages
					};

					if (existingIndex !== -1) {
						const updatedMessages = [...prevMessages];
						updatedMessages[existingIndex] = errorMessage;
						return updatedMessages;
					} else {
						return [...prevMessages, errorMessage];
					}
				});
				break; // Exit the loop as we've encountered an error
			}

			if (isNotificationMode && parsedData.event === "notification") {
				setMessages((prevMessages) => {
					const existingIndex = prevMessages.findIndex((msg) => msg.sectionId === payload?.section_id);
					const messageText = parsedData.data.trim();
					const existingOrder = existingIndex !== -1 ? prevMessages[existingIndex]?.order ?? null : null;

					const newNotificationMessage = {
						text: messageText,
						sectionId: payload?.section_id,
						type: QueryMessageType.received,
						is_notification: true,
						order: existingOrder,
					};

					if (existingIndex !== -1) {
						const updatedMessages = [...prevMessages];
						updatedMessages[existingIndex] = newNotificationMessage;
						return updatedMessages;
					} else {
						return [...prevMessages, newNotificationMessage];
					}
					return prevMessages;
				});
			} else if (parsedData.event === "from_db") {
				isDataFromDB = true;
				continue;
			} else if (parsedData.event === "section_content") {
				isNotificationMode = false;
				combinedData += parsedData.data;

				setMessages((prevMessages) => {
					const existingIndex = prevMessages.findIndex((msg) => msg.sectionId === payload?.section_id);

					const messageText = isDataFromDB ? parsedData.data : combinedData.trim();
					const existingOrder = existingIndex !== -1 ? prevMessages[existingIndex]?.order ?? null : null;

					const newContentMessage = {
						text: messageText,
						sectionId: payload?.section_id,
						type: QueryMessageType.received,
						is_notification: false,
						order: existingOrder,
					};

					if (existingIndex !== -1) {
						const updatedMessages = [...prevMessages];
						updatedMessages[existingIndex] = newContentMessage;
						return updatedMessages;
					} else {
						return [...prevMessages, newContentMessage];
					}
				});
			} else if (parsedData.event === "references") {
				try {
					const refData = JSON.parse(parsedData.data);

					const mapRef = (refs = []) =>
						refs.map((ref) => ({
							...ref,
							is_study_access_available: true,
							display_message: "",
						}));

					if (refData.reports || refData.transcripts) {
						dispatch(
							setReportsReferences({
								reports: mapRef(refData.reports),
								transcripts: mapRef(refData.transcripts),
							})
						);
					}
				} catch (error) {
					console.error("Error parsing references data:", error);
				}
				continue;
			}
		}
	} catch (error) {
		if (error?.name === "AbortError") {
			console.warn("Streaming response aborted:", error);
		} else {
			console.error("Error processing streaming response:", error);
		}
		setSections((prev) => prev?.map((sec) => (sec.section_id === payload?.section_id ? { ...sec, display_on_ui: fileStatus.failed, is_edited: true } : sec)));
	} finally {
		dispatch(cleanupAbortControllerReports(payload?.section_id));
	}
};

export const getStatusColor = (status: string) => {
	switch (status.toLowerCase()) {
		case "published":
			return "var(--primary-color-opacity-15)";
		case "draft":
			return "#FCA32640";
		case "archived":
			return "#d9d9d9";
		default:
			return "var(--neutral-grey-opacity-15)";
	}
};

export const formatDate = (dateString: string) => {
	const date = new Date(dateString);
	const day = date.getDate();
	const month = date.toLocaleString("default", { month: "long" });
	const year = date.getFullYear();

	const getOrdinalSuffix = (day: number) => {
		if (day > 3 && day < 21) return "th";
		switch (day % 10) {
			case 1:
				return "st";
			case 2:
				return "nd";
			case 3:
				return "rd";
			default:
				return "th";
		}
	};

	return {
		month,
		day,
		suffix: getOrdinalSuffix(day),
		year,
	};
};

export const calculateDataSourceTotals = (
	dataSources: {
		entity_id?: string[];
		file_id?: string[];
		meta_id?: string[];
	},
	dataConfig: IDataConfig
) => {
	// Handle both old and new data source formats
	const entityIds = dataSources?.entity_id || [];
	const fileIds = dataSources?.file_id || [];
	const metaIds = dataSources?.meta_id || [];

	const uniqueStudies = new Set<string>();
	const uniqueDatasets = new Set<string>();

	// Process entity_ids (report_iteration_ids where all datasets are selected)
	entityIds.forEach((studyId) => {
		Object.keys(dataConfig).forEach((type) => {
			const study = dataConfig[type]?.find((s: any) => s.study_id === studyId);
			if (study) {
				uniqueStudies.add(study.study);
				// Add all datasets from this study
				study.datasets?.forEach((d: any) => {
					uniqueDatasets.add(d.key);
				});
			}
		});
	});

	// Process file_ids (individual dataset keys)
	fileIds.forEach((datasetKey) => {
		uniqueDatasets.add(datasetKey);
		// Find which study this dataset belongs to
		Object.keys(dataConfig).forEach((type) => {
			dataConfig[type]?.forEach((study: any) => {
				if (study.datasets?.some((d: any) => d.key === datasetKey)) {
					uniqueStudies.add(study.study);
				}
			});
		});
	});

	return {
		totalStudies: uniqueStudies.size,
		totalDatasets: uniqueDatasets.size,
		totalTags: metaIds.length,
	};
};

export const hasUnsavedChangesReports = (autosaveStatus: { status: AutosaveStatusType | null; lastSaved: string | null }) => {
	return (
		(autosaveStatus?.status === AutosaveStatusType.Error ||
			autosaveStatus?.status === AutosaveStatusType.UnsavedChanges ||
			autosaveStatus?.status === AutosaveStatusType.ValidationError ||
			// autosaveStatus?.status === AutosaveStatusType.ToBeSaved ||
			autosaveStatus?.status === AutosaveStatusType.Saving) &&
		autosaveStatus?.status !== null
	);
};

export const getStatusText = (status: string) => {
	return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

export const useFilterReports = ({
	reportsData,
	searchText,
	selectedCollaborators,
	selectedReportsPageTab,
	metaDetails,
	selectedTag,
}: IFilterReportsProps & {
	selectedReportsPageTab: ReportsHomePageTabType;
	metaDetails: IMetaDetails;
}) => {
	// Create a searchIndex for optimized text searching
	const searchIndex = useMemo(() => {
		return reportsData.map((report) => ({
			report,
			searchableText: [report?.name?.toLowerCase(), ...(report?.permissions?.collaborators?.map((collab) => collab?.collaborator_name?.toLowerCase()) || [])].join(" "),
		}));
	}, [reportsData]);

	// Step 1: Filter by search text and collaborators
	const filteredReports = useMemo(() => {
		// Early return if no filters applied
		if (!searchText && (!selectedCollaborators || selectedCollaborators.length === 0) && selectedTag === "All") {
			return reportsData;
		}

		// Initialize result array
		const results: IReport[] = [];
		const addedIds = new Set<string>();

		// Process search text filter (if any)
		if (searchText) {
			const searchQueries = searchText
				.split(";")
				.map((query) => query.trim().toLowerCase())
				.filter((query) => query.length > 0);

			if (searchQueries.length > 0) {
				// Use pre-computed search index for faster search
				searchIndex.forEach(({ searchableText, report }) => {
					if (searchQueries.some((query) => searchableText.includes(query))) {
						if (!addedIds.has(report.report_iteration_id)) {
							results.push(report);
							addedIds.add(report.report_iteration_id);
						}
					}
				});
			}
		}

		// Process collaborator filter (if any)
		if (selectedCollaborators?.length) {
			const lowerCaseSelectedCollaborators = selectedCollaborators.map((collab) => collab.collaborator.toLowerCase());

			reportsData.forEach((report) => {
				if (report?.permissions?.collaborators.some((collaborator) => lowerCaseSelectedCollaborators.includes(collaborator.collaborator_name.toLowerCase()))) {
					if (!addedIds.has(report.report_iteration_id)) {
						results.push(report);
						addedIds.add(report.report_iteration_id);
					}
				}
			});
		}

		// Process tag filter
		if (selectedTag !== "All") {
			const tagFilteredResults = results.length > 0 ? results : reportsData;
			return tagFilteredResults.filter((study) => study.custom_tags?.includes(selectedTag));
		}

		// Return results, or original data if no results
		return results.length > 0 ? results : reportsData;
	}, [reportsData, searchText, selectedCollaborators, selectedTag, searchIndex]);

	// Step 2: Apply tab-based filtering
	const finalFilteredReports = useMemo(() => {
		switch (selectedReportsPageTab) {
			case ReportsHomePageTabType.Recent:
				// Sort reports by most recently created/modified and return top 10
				return filteredReports.slice(0, 6);

			case ReportsHomePageTabType.Starred:
				// Filter reports where the current user has starred the report
				return filteredReports.filter((report) =>
					report?.permissions?.collaborators.some((collab) => collab.collaborator_id === metaDetails?.id && collab.is_star === true)
				);

			case ReportsHomePageTabType.All:
			default:
				return filteredReports;
		}
	}, [filteredReports, selectedReportsPageTab, metaDetails?.id]);

	return {
		filteredReports,
		finalFilteredReports,
	};
};
